import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import { MdEmail, MdLock, MdVisibility, MdVisibilityOff } from "react-icons/md";
import { IoAlertCircleSharp } from "react-icons/io5";
import {
  FaShieldAlt,
  FaGraduationCap,
  FaUserTie,
  FaChartLine,
  FaCheckCircle,
  FaArrowRight,
} from "react-icons/fa";
import useAuth from "../useAuth";
import Loading from "../Components/global/Loading";

export default function LoginPage() {
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const { user, authMessage, loading, loginWithEmailAndPassword } = useAuth();
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async (data) => {
    loginWithEmailAndPassword(data?.email, data?.password);
  };

  if (user) {
    return <Navigate to="/my/" />;
  }

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-950 overflow-hidden font-sans">
      {/* Left Side - Modern Hero Section */}
      <motion.div
        initial={{ x: -60, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="hidden lg:flex w-7/12 relative overflow-hidden flex-col justify-between p-12 text-white bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-900"
      >
        {/* Animated Background Mesh Pattern */}
        <div
          className="absolute inset-0 opacity-15 pointer-events-none"
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)`,
            backgroundSize: "32px 32px",
          }}
        />

        {/* Ambient Glowing Orbs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-32 -left-32 w-96 h-96 bg-indigo-500/25 rounded-full blur-3xl animate-pulse" />
          <div className="absolute top-1/2 -right-20 w-80 h-80 bg-sky-500/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-20 left-1/3 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl" />
        </div>

        {/* Top Header Badge */}
        <div className="relative z-10 flex items-center justify-between">
          <div className="flex items-center gap-3 bg-white/10 backdrop-blur-md px-4 py-2 rounded-2xl border border-white/15 shadow-xl">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-tr from-sky-400 to-indigo-500 flex items-center justify-center text-white shadow-md">
              <FaShieldAlt size={16} />
            </div>
            <div>
              <span className="text-xs font-black tracking-wider uppercase text-sky-200">
                Dee Novo School
              </span>
              <span className="block text-[10px] font-bold text-slate-300">
                Management System
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2 text-xs font-bold text-slate-300 bg-slate-900/60 px-3 py-1.5 rounded-full border border-slate-800">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
            <span>Secure SSL Encrypted</span>
          </div>
        </div>

        {/* Center Content & Branding */}
        <div className="relative z-10 max-w-xl my-auto py-12">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="mb-8 p-4 bg-white/95 dark:bg-slate-900/90 rounded-3xl shadow-2xl border border-white/20 inline-block backdrop-blur-xl"
          >
            <img
              src="/assets/images/logo.jpg"
              alt="Dee Novo School Logo"
              className="h-20 sm:h-24 w-auto object-contain rounded-2xl"
            />
          </motion.div>

          <motion.h1
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-4xl lg:text-5xl font-black mb-4 tracking-tight leading-tight bg-gradient-to-r from-white via-slate-100 to-sky-200 bg-clip-text text-transparent"
          >
            Administrative & Academic Control Center
          </motion.h1>

          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-base text-slate-300 leading-relaxed font-medium max-w-lg mb-8"
          >
            Streamline school operations, monitor real-time academic records, manage user roles, and govern permissions seamlessly.
          </motion.p>

          {/* Feature Highlights */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="grid grid-cols-2 gap-4 pt-4 border-t border-white/10"
          >
            <div className="flex items-center gap-3 bg-white/5 p-3 rounded-2xl border border-white/10 backdrop-blur-xs">
              <div className="p-2 rounded-xl bg-sky-500/20 text-sky-300">
                <FaGraduationCap size={16} />
              </div>
              <div className="text-xs">
                <p className="font-black text-white">Academics & Students</p>
                <p className="text-[10px] text-slate-400 font-semibold">Student Profiles & Records</p>
              </div>
            </div>

            <div className="flex items-center gap-3 bg-white/5 p-3 rounded-2xl border border-white/10 backdrop-blur-xs">
              <div className="p-2 rounded-xl bg-indigo-500/20 text-indigo-300">
                <FaUserTie size={16} />
              </div>
              <div className="text-xs">
                <p className="font-black text-white">Staff Management</p>
                <p className="text-[10px] text-slate-400 font-semibold">Teachers & Staff Profiles</p>
              </div>
            </div>

            <div className="flex items-center gap-3 bg-white/5 p-3 rounded-2xl border border-white/10 backdrop-blur-xs">
              <div className="p-2 rounded-xl bg-emerald-500/20 text-emerald-300">
                <FaShieldAlt size={16} />
              </div>
              <div className="text-xs">
                <p className="font-black text-white">Granular Access</p>
                <p className="text-[10px] text-slate-400 font-semibold">Role Access Matrix</p>
              </div>
            </div>

            <div className="flex items-center gap-3 bg-white/5 p-3 rounded-2xl border border-white/10 backdrop-blur-xs">
              <div className="p-2 rounded-xl bg-purple-500/20 text-purple-300">
                <FaChartLine size={16} />
              </div>
              <div className="text-xs">
                <p className="font-black text-white">Fees & Analytics</p>
                <p className="text-[10px] text-slate-400 font-semibold">Financial Reports</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Footer Tagline */}
        <div className="relative z-10 flex items-center justify-between text-xs font-semibold text-slate-400 pt-6 border-t border-white/10">
          <span>© {new Date().getFullYear()} Dee Novo School ERP</span>
          <span className="flex items-center gap-1.5 text-slate-300">
            <FaCheckCircle className="text-emerald-400" size={12} /> Enterprise Grade Security
          </span>
        </div>
      </motion.div>

      {/* Right Side - Sleek Login Card Form */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className="w-full lg:w-5/12 flex items-center justify-center p-6 sm:p-12 lg:p-16 bg-white dark:bg-slate-900"
      >
        <div className="w-full max-w-md space-y-8">
          {/* Header */}
          <div className="text-center lg:text-left">
            <div className="lg:hidden inline-block mb-4 p-3 bg-indigo-50 dark:bg-slate-800 rounded-2xl">
              <img
                src="/assets/images/logo.jpg"
                alt="Dee Novo School Logo"
                className="h-14 w-auto object-contain rounded-xl"
              />
            </div>
            <h2 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">
              Sign In to Portal
            </h2>
            <p className="mt-2 text-slate-500 dark:text-slate-400 text-xs font-semibold">
              Enter your official administrative credentials to access your portal.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(handleLogin)} className="space-y-5">
            {/* Email Field */}
            <div className="space-y-1.5">
              <label className="text-xs font-black uppercase text-slate-600 dark:text-slate-300 tracking-wider ml-1">
                Email Address
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-indigo-600 transition-colors">
                  <MdEmail className="text-lg" />
                </div>
                <input
                  type="email"
                  className={`block w-full pl-11 pr-4 py-3 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition-all duration-200 outline-none text-sm font-semibold ${
                    errors.email ? "border-rose-400 bg-rose-50/50" : ""
                  }`}
                  placeholder="admin@deenovo.com"
                  {...register("email", {
                    required: "Email address is required",
                    pattern: {
                      value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                      message: "Please enter a valid email address",
                    },
                  })}
                />
              </div>
              {errors.email && (
                <p className="text-xs text-rose-500 font-bold flex items-center gap-1 ml-1 animate-pulse">
                  <IoAlertCircleSharp /> {errors.email.message}
                </p>
              )}
            </div>

            {/* Password Field */}
            <div className="space-y-1.5">
              <div className="flex justify-between items-center ml-1">
                <label className="text-xs font-black uppercase text-slate-600 dark:text-slate-300 tracking-wider">
                  Password
                </label>
                <a
                  href="#forgot"
                  onClick={(e) => {
                    e.preventDefault();
                    alert("Please contact system administrator to reset your password.");
                  }}
                  className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline font-bold transition-colors"
                >
                  Forgot Password?
                </a>
              </div>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-indigo-600 transition-colors">
                  <MdLock className="text-lg" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  className={`block w-full pl-11 pr-11 py-3 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition-all duration-200 outline-none text-sm font-semibold ${
                    errors.password ? "border-rose-400 bg-rose-50/50" : ""
                  }`}
                  placeholder="••••••••"
                  {...register("password", {
                    required: "Password is required",
                    minLength: {
                      value: 6,
                      message: "Min 6 characters required",
                    },
                  })}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <MdVisibilityOff className="text-lg" />
                  ) : (
                    <MdVisibility className="text-lg" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="text-xs text-rose-500 font-bold flex items-center gap-1 ml-1 animate-pulse">
                  <IoAlertCircleSharp /> {errors.password.message}
                </p>
              )}
            </div>

            {/* Error Message Alert */}
            {authMessage && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300 px-4 py-3 rounded-xl flex items-center gap-3 text-xs font-bold shadow-xs"
              >
                <IoAlertCircleSharp className="text-lg shrink-0 text-rose-500" />
                <span>
                  {authMessage.slice(5).split("-").join(" ")}
                </span>
              </motion.div>
            )}

            {/* Submit Button */}
            <div className="pt-2">
              <motion.button
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-indigo-600 via-indigo-700 to-sky-600 hover:from-indigo-700 hover:to-sky-700 text-white font-black py-3.5 px-4 rounded-xl shadow-lg shadow-indigo-500/25 transition-all duration-300 flex items-center justify-center gap-2 text-xs uppercase tracking-wider disabled:opacity-50 cursor-pointer"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Authenticating...
                  </>
                ) : (
                  <>
                    Sign In to Account <FaArrowRight size={12} />
                  </>
                )}
              </motion.button>
            </div>

            {/* Help & Contact Support */}
            <div className="text-center pt-4 text-slate-400 dark:text-slate-500 text-xs font-semibold">
              Need account access or credentials?{" "}
              <button
                type="button"
                onClick={() => navigate("/register")}
                className="text-indigo-600 dark:text-indigo-400 font-black hover:underline focus:outline-none transition-colors"
              >
                Request Access
              </button>
            </div>
          </form>
        </div>
      </motion.div>

      {loading && <Loading />}
    </div>
  );
}

